"use client";

import { useEffect, useRef } from "react";
import { debounce, isDocumentDefined } from "@huub-web/utils";

type ResizeObserverCallback = (
  entries: ResizeObserverEntry[],
  observer: ResizeObserver,
) => void;

const DEFAULT_OPTIONS: ResizeObserverOptions = { box: "border-box" };

export const useResizeObserver = <T = HTMLElement>(
  callback: ResizeObserverCallback,
  options: ResizeObserverOptions & { debounce?: number } = {},
) => {
  const ref = useRef<T>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const callbackRef = useRef(callback);
  const optionsRef = useRef(options);

  // Update refs when props change
  callbackRef.current = callback;
  optionsRef.current = options;

  useEffect(() => {
    if (!ref.current || !isDocumentDefined()) {
      return;
    }

    const { debounce: debounceTime, ...resizeOptions } = {
      ...DEFAULT_OPTIONS,
      ...optionsRef.current,
    };

    const handleResize: ResizeObserverCallback = (entries, obs) => {
      if (callbackRef.current) {
        callbackRef.current(entries, obs);
      }
    };

    const debouncedCallback = debounceTime
      ? debounce(handleResize, debounceTime)
      : handleResize;

    observer.current = new ResizeObserver(debouncedCallback);
    observer.current.observe(ref.current as unknown as Element, resizeOptions);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []); // Empty dependency array - only run once

  return { ref };
};
